// services/chatStream/frameScheduler.ts
//
// Run a callback at most once per painted frame.
//
// A streaming answer arrives as one `text.delta` per chunk, and doing a Redux
// round trip and a layout read for each of them is work the reader never sees:
// the screen repaints once a frame whatever we do (ORA-459). Everything that
// coalesces per frame — the slice dispatch and the thread's scroll follow —
// goes through this, so they settle together.

/** Fallback cadence where there is no `requestAnimationFrame`. ~60 Hz. */
const FALLBACK_FRAME_MS = 16;

export interface FrameScheduler {
  /**
   * Ask for `callback` to run on the next frame. Calling again before that
   * frame arrives replaces the callback and does not add a second run — and
   * does not push the run back either: the pending run keeps the timing it was
   * armed with, so a steady stream of requests is rate-limited to one run a
   * frame rather than debounced into never running at all (ORA-459).
   */
  schedule: (callback: () => void) => void;
  /** Drop a pending run. Safe when nothing is pending. */
  cancel: () => void;
}

/**
 * `requestAnimationFrame` when the host has one, a timer otherwise.
 *
 * The timer path is not only for tests: a background tab stops painting, and
 * `requestAnimationFrame` there can be parked for minutes — long enough for a
 * whole answer to arrive with nothing applied.
 */
export const createFrameScheduler = (): FrameScheduler => {
  const hasRaf =
    typeof requestAnimationFrame === 'function' &&
    typeof cancelAnimationFrame === 'function';

  let handle: number | null = null;
  let isFrame = false;
  let pending: (() => void) | null = null;

  const cancel = () => {
    pending = null;
    if (handle === null) return;

    if (isFrame) cancelAnimationFrame(handle);
    else clearTimeout(handle);

    handle = null;
  };

  const run = () => {
    handle = null;
    const callback = pending;
    pending = null;
    callback?.();
  };

  return {
    schedule: (callback) => {
      pending = callback;

      // A run is already armed: it will pick this callback up when it fires.
      // Re-arming here is what turned the timer path into a debounce — a
      // hidden tab receiving a delta every few milliseconds pushed the flush
      // back on every one of them and never applied a word.
      if (handle !== null) return;

      if (hasRaf && typeof document !== 'undefined' && !document.hidden) {
        isFrame = true;
        handle = requestAnimationFrame(run);
        return;
      }

      isFrame = false;
      handle = setTimeout(run, FALLBACK_FRAME_MS) as unknown as number;
    },
    cancel,
  };
};
